import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { BookOpen, ArrowRight } from "lucide-react";
import { useAuthStore } from "@/features/auth/auth-store";
import { ModuleCard } from "@/features/learn/components/ModuleCard";
import { ModuleExam } from "@/features/learn/components/ModuleExam";
import { WeeklyLeaderboard } from "@/features/learn/components/WeeklyLeaderboard";
import { PlacementTest } from "@/features/learn/components/PlacementTest";
import { getAllModules, getAllLessons, getModule } from "@/features/learn/content";
import { fetchProgress, completeModuleExam } from "@/features/learn/api";
import { getProgress, mergeServerProgress, markCompleted } from "@/features/learn/progress-store";
import { lt } from "@/features/learn/localize";
import type { LessonProgress } from "@/features/learn/types";

const PLACEMENT_DONE_KEY = "placement-done";

const Learn = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);

  // Read local progress immediately (always fresh on mount/navigation)
  const [lessonProgress, setLessonProgress] = useState<
    Record<string, LessonProgress>
  >(() => getProgress());
  const [loading, setLoading] = useState(true);
  const [examModuleId, setExamModuleId] = useState<string | null>(null);
  const [showPlacementTest, setShowPlacementTest] = useState(false);

  useEffect(() => {
    // Refresh from localStorage on every navigation to /learn
    setLessonProgress(getProgress());

    if (!token) {
      setLoading(false);
      return;
    }
    // Also try to merge with server data
    fetchProgress(token)
      .then((data) => {
        const merged = mergeServerProgress(data.progress);
        setLessonProgress(merged);
      })
      .catch(() => {
        // Server unavailable — local progress still works
      })
      .finally(() => setLoading(false));
  }, [token, location.key]);

  // Show placement test if user has no progress and hasn't completed it
  useEffect(() => {
    if (loading) return;
    const hasProgress = Object.keys(lessonProgress).length > 0;
    const placementDone = localStorage.getItem(PLACEMENT_DONE_KEY) === "true";
    if (!hasProgress && !placementDone && token) {
      setShowPlacementTest(true);
    }
  }, [loading, lessonProgress, token]);

  const handlePlacementComplete = (_level: number) => {
    localStorage.setItem(PLACEMENT_DONE_KEY, "true");
    setShowPlacementTest(false);
    // Re-read progress after marking lessons as completed
    setLessonProgress(getProgress());
  };

  const handlePlacementSkip = () => {
    localStorage.setItem(PLACEMENT_DONE_KEY, "true");
    setShowPlacementTest(false);
  };

  const modules = getAllModules();
  const allLessons = getAllLessons();
  const totalLessons = allLessons.length;
  const completedLessons = allLessons.filter(
    (l) => lessonProgress[l.id]?.completed,
  ).length;
  const progressPercent =
    totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;

  // Find the next uncompleted lesson
  const nextUncompleted = allLessons.find(
    (l) => !lessonProgress[l.id]?.completed,
  );

  // Check if a module is unlocked: its requiredModuleId must be fully complete
  const isModuleUnlocked = (moduleId: string): boolean => {
    const mod = modules.find((m) => m.id === moduleId);
    if (!mod?.requiredModuleId) return true;
    const req = modules.find((m) => m.id === mod.requiredModuleId);
    if (!req || req.lessons.length === 0) return true;
    return req.lessons.every((l) => lessonProgress[l.id]?.completed);
  };

  const isModuleComplete = (moduleId: string): boolean => {
    const mod = modules.find((m) => m.id === moduleId);
    if (!mod || mod.lessons.length === 0) return false;
    return mod.lessons.every((l) => lessonProgress[l.id]?.completed);
  };

  const handleExamComplete = async (score: number, passed: boolean) => {
    if (!examModuleId || !token) return;
    const mod = getModule(examModuleId);
    if (!mod) return;

    const lessonIds = mod.lessons.map((l) => l.id);
    try {
      await completeModuleExam(token, examModuleId, lessonIds, score, passed);
    } catch {
      // best effort
    }

    if (passed) {
      // Update local progress — markCompleted writes to localStorage
      let updated = { ...lessonProgress };
      for (const lid of lessonIds) {
        updated = markCompleted(lid, 0, 1);
      }
      // Re-read from localStorage to get the latest
      setLessonProgress(getProgress());
    }

    setExamModuleId(null);
  };

  // Show placement test overlay
  if (showPlacementTest) {
    return (
      <PlacementTest
        onComplete={handlePlacementComplete}
        onSkip={handlePlacementSkip}
      />
    );
  }

  // Show exam view
  if (examModuleId) {
    const mod = modules.find((m) => m.id === examModuleId);
    if (mod?.exam) {
      return (
        <ModuleExam
          module={mod}
          onComplete={handleExamComplete}
          onCancel={() => setExamModuleId(null)}
        />
      );
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {t("learn_loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="p-4 space-y-5 max-w-screen-xl mx-auto">
        {/* Overall progress */}
        <Card className="glass p-5 border-secondary/20">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-bold text-lg">{t("learn_progress")}</h2>
            <span className="text-2xl font-bold text-accent">
              {Math.round(progressPercent)}%
            </span>
          </div>
          <Progress value={progressPercent} className="h-3 mb-2" />
          <p className="text-sm text-muted-foreground">
            {completedLessons} / {totalLessons} {t("learn_lessons_completed")}
          </p>
        </Card>

        {/* Continue learning CTA */}
        {nextUncompleted && (
          <Card className="glass p-5 border-secondary/30">
            <Badge className="bg-secondary text-secondary-foreground mb-3">
              {t("learn_continue")}
            </Badge>
            <h3 className="text-lg font-bold mb-1">
              {lt(nextUncompleted.title, lang)}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {lt(nextUncompleted.subtitle, lang)}
            </p>
            <Button
              className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold"
              onClick={() => navigate(`/learn/${nextUncompleted.id}`)}
            >
              <span className="flex items-center gap-2">
                {lessonProgress[nextUncompleted.id]
                  ? t("learn_resume")
                  : t("learn_start")}
                <ArrowRight className="w-4 h-4" />
              </span>
            </Button>
          </Card>
        )}

        {/* Modules list */}
        <section>
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {t("learn_modules")}
          </h2>
          <div className="space-y-3">
            {modules.map((mod) => (
              <ModuleCard
                key={mod.id}
                module={mod}
                lessonProgress={lessonProgress}
                isLocked={!isModuleUnlocked(mod.id)}
                isComplete={isModuleComplete(mod.id)}
                onTakeExam={setExamModuleId}
              />
            ))}
          </div>
        </section>

        {/* Weekly Leaderboard */}
        {token && <WeeklyLeaderboard />}
      </div>
    </div>
  );
};

export default Learn;
